<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';

import type {
  FormSchema,
  VFormRef,
} from '@modules/forms/components/VForm/types';
import type { IProfileSettingsChangePasswordSchema } from '@modules/profile-settings/submodules/change-password/types';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import {
  PROFILE_SETTINGS_PERMISSIONS,
  TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS,
  TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS,
} from '@modules/profile-settings/constants';
import { useProfileSettingsChangePasswordMutation } from '@modules/profile-settings/submodules/change-password/mutations';
import { useUserSupervision } from '@modules/user/composables';

const { isAdminNamespace } = useCurrentNamespace();
const { getTranslation } = useI18n();
const { isSupervision } = useUserSupervision();

const {
  mutateAsync,
  isLoading: isLoadingSave,
  errors,
  clearError,
} = useProfileSettingsChangePasswordMutation();
const form = ref<VFormRef>();

const translationGroup = computed(() => (
  isAdminNamespace.value ? TRANSLATION_GROUP_ADMIN_PROFILE_SETTINGS : TRANSLATION_GROUP_AFFILIATE_PROFILE_SETTINGS
));

const schema = computed<FormSchema<IProfileSettingsChangePasswordSchema>>(() => ({
  password: {
    type: EFormInputTypes.PASSWORD,
    label: getTranslation(translationGroup.value, 'PASSWORD_PASSWORD').value,
    required: true,
    disabled: isSupervision.value,
    autocomplete: 'new-password',
  },
  password_confirmation: {
    type: EFormInputTypes.PASSWORD,
    label: getTranslation(translationGroup.value, 'PASSWORD_PASSWORD_CONFIRMATION').value,
    required: true,
    disabled: isSupervision.value,
    autocomplete: 'new-password',
  },
  current_password: {
    type: EFormInputTypes.PASSWORD,
    label: getTranslation(translationGroup.value, 'PASSWORD_CURRENT_PASSWORD').value,
    required: true,
    disabled: isSupervision.value,
    autocomplete: 'current-password',
  },
}));

async function onSubmit(profile: IProfileSettingsChangePasswordSchema): Promise<void> {
  await mutateAsync({ profile });
  form.value?.reset();
}
</script>

<template>
  <VForm v-data-qa-auto-id="'ChangePasswordPage'"
    ref="form"
    :schema="schema"
    :readonly="!$hasPermission(PROFILE_SETTINGS_PERMISSIONS.EDIT) || isSupervision"
    :submit-btn-label="$getTranslation(translationGroup, 'SAVE')"
    :custom-errors="errors"
    :is-loading="isLoadingSave"
    @interacted="clearError"
    @submit="onSubmit"
  />
</template>
